import React from "react";
import { connect } from 'react-redux';
import mapboxgl from "mapbox-gl";
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import * as turf from '@turf/turf';

import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { toggleSwitch,
        selectedCardsGroupsChange, 
        popUpMenuChange, 
        visibleCardsGroupsChange,
        visibleWhatsAppObservationsChange,
        setBreakCoordinatesModal,
        deleteCards,
        popUpMessageChange,
        editFigures,
        editCards,
        editFeaturesGroup,
        setSelectedWhatsAppObservations,
        setWhatsAppObservationsPopup} from "../../actions/index.js";

import { sky, 
        // map3d,
        // terrainExaggeration,
        projectLevelLayer,
        projectLevelSource,
        createFeatureCollection,
        cardPointLayer, 
        cardLineStringLayer,
        cardPolygonLayer,
        cardOutlinePolygonLayer,
        getLevelData, 
        axleNamesLayer,
        axlesLineLayer } from "../../functions/map/mapboxDataSource.js";

import { getCardMarker } from './mapMarkers';
import mapManager from "../../functions/map/mapManager.js";
import { addHighlight, highlightManager,
        removeHighlight } from "../../functions/map/highlightManager.js";
import hoverManager from "../../functions/map/hoverManager.js";
import { getFeaturesCardsGroupIds,
        getHoveredFeatures,
        whatsAppObservationFiltersPass } from "../../functions/cardsGroupsFeatures.js";
import { ToggleActions, allSelectableLayers } from "../../constants/constants.js";
import { getAxleFeatures } from "../../functions/map/axlesManager.js";
import { isdevelopment } from "../propsManager.js";


class Mapbox extends React.Component  {
    constructor(props) {
        super(props);
        this.state = {
          roundedArea: null
        };
        this.draw = new MapboxDraw({
            displayControlsDefault: false,
            controls: {
                polygon: true,
                trash: true,
            },
            defaultMode: 'draw_polygon'
        });
    }
    

    componentDidMount() {
        this.initializeMap()
    }

    updateDrawControls(createBreakMode) {
        if (createBreakMode) {
            this.map.addControl(this.draw);
        } else {
            this.map.removeControl(this.draw);
        }
    }

    initializeMap() {
        const defaultZoom = 14;
        this.map = new mapboxgl.Map({
            container: this.mapContainer,
            style: this.props.mapStyle,
            zoom: defaultZoom,
            minZoom: defaultZoom,
            accessToken: process.env.REACT_APP_MAPBOX_TOKEN,
            maxPitch: 80,
            attributionControl: false,
            center: this.props.projectData.attributes.coordinates.center,
            boxZoom: false
        })
        const updateArea = (e) => {
            const data = this.draw.getAll();
            if (data.features.length > 0) {
                const area = turf.area(data);
                this.setState({ roundedArea: Math.round(area * 100) / 100 });
            } else {
                this.setState({ roundedArea: null });
                if (e.type !== 'draw.delete') {
                alert('Click the map to draw a polygon.');
                }
            }
        }
    
        this.map.on('draw.create', (e) => {
            //updateArea(e);
            const coordinates = e.features[0].geometry.coordinates[0];
            this.props.setBreakCoordinatesModal({coordinates:coordinates.slice(0, -1), show:true})
        });
        this.map.on('draw.delete', updateArea);
        this.map.on('draw.update', updateArea);

        let cardMarker = getCardMarker()
        let levelId = this.props.loadedLevels[0].toString()
        let levelData = getLevelData(this.props.projectData, this.props.projectData.levels[levelId])
        let [axleFeatures, axleNamesFeatures] = getAxleFeatures(this.props.projectData.axles, this.props.projectData.levels)

        this.map.on('load', () => {
            //this.map.setTerrain({ source: map3d, exaggeration: terrainExaggeration })
            this.map.addLayer(sky)
            this.map.addSource('projectLevel', projectLevelSource(levelData))
            this.map.addLayer(projectLevelLayer)
            if ( !isdevelopment ){
                this.map.addImage('cardMarker', cardMarker, { sdf: true })
            }
            this.map.addSource('cards', createFeatureCollection([]))
            this.map.addSource('axles', createFeatureCollection(axleFeatures))
            this.map.addSource('axleNames', createFeatureCollection(axleNamesFeatures))
            this.map.addLayer(axlesLineLayer)
            this.map.addLayer(axleNamesLayer)
            this.map.addLayer(cardOutlinePolygonLayer)
            this.map.addLayer(cardPolygonLayer)
            this.map.addLayer(cardLineStringLayer)
            this.map.addLayer(cardPointLayer)
            this.props.toggleSwitch(ToggleActions.LOADED_MAP)
        })
        let isEmbedMode = typeof window !== 'undefined' && window.embed === true;
        if ( !isEmbedMode ) {
            this.map.on('click', this.handleOpenPopup)
            hoverManager(this.map)
            highlightManager(this.map, {
                ...this.props,
                setWhatsAppObservationsPopup: this.props.setWhatsAppObservationsPopup,
                setSelectedWhatsAppObservations: this.props.setSelectedWhatsAppObservations,
            }, this.handleOpenPopup)
        }
    }

    handleOpenPopup = (e) => {
        removeHighlight(this.map)
        let bbox = [
            [e.point.x - 5, e.point.y - 5],
            [e.point.x + 5, e.point.y + 5]
        ];
        const selectedFeatures = this.map.queryRenderedFeatures(bbox, { layers: allSelectableLayers });
        const hoveredFeatures = getHoveredFeatures(selectedFeatures);
        if (hoveredFeatures.length) {
            // Check if WhatsApp observations mode is active
            if (this.props.whatsappObservationsMode) {
                // Get figuregroup IDs from features
                const { figuregroupIds, levelId } = this.getDataFromFeatures(hoveredFeatures);
                if (figuregroupIds.length === 0) {
                    return;
                }
                
                // Get WhatsApp observations for these figuregroups
                const observationIds = this.getWhatsAppObservationIdsFromFiguregroups(figuregroupIds, levelId);
                if (observationIds.length === 0) {
                    return;
                }
                
                addHighlight(this.map, hoveredFeatures);
                this.props.setSelectedWhatsAppObservations(observationIds);
                this.props.setWhatsAppObservationsPopup({ show: true });
            } else {
                // show cardsgroups
                let cardsGroupIds = getFeaturesCardsGroupIds(this.map, hoveredFeatures);
                if (cardsGroupIds.length === 0) return;
                addHighlight(this.map, hoveredFeatures);
                this.props.popUpMenuChange({ show: true });
                this.props.selectedCardsGroupsChange(cardsGroupIds);
            }
        }
    }

    getDataFromFeatures = (features) => {
        let figuregroupIds = [];
        let levelId = null;
        for (const feature of features) {
            if (feature.properties) {
                // Handle both figuregroup_id (single) and figuregroup_ids (comma-separated string)
                if (feature.properties.figuregroup_id) {
                    const figuregroupId = feature.properties.figuregroup_id;
                    levelId = feature.properties.level_id;
                    if (!figuregroupIds.includes(figuregroupId)) {
                        figuregroupIds.push(figuregroupId);
                    }
                } else if (feature.properties.figuregroup_ids) {
                    // Parse comma-separated string like ",123,456,"
                    const ids = feature.properties.figuregroup_ids
                        .split(',')
                        .filter(id => id !== '' && id !== null && id !== undefined)
                        .map(id => parseInt(id))
                        .filter(id => !isNaN(id));
                    levelId = feature.properties.level_id;
                    ids.forEach(id => {
                        if (!figuregroupIds.includes(id)) {
                            figuregroupIds.push(id);
                        }
                    });
                } 
            }
        }
        return { figuregroupIds, levelId };
    }

    getWhatsAppObservationIdsFromFiguregroups = (figuregroupIds, levelId) => {
        const observationIds = [];
        const observations = this.props.whatsappObservations.observations || {};
        
        
        // Convert to Set for O(1) lookup instead of O(n) with includes()
        const figuregroupIdsSet = new Set(figuregroupIds.map(id => String(id)));
        const levelIdStr = String(levelId);
        
        for (const observationId in observations) {
            const observation = observations[observationId];
            if (!whatsAppObservationFiltersPass(this.props.whatsappFilters, observation)) continue;
            if (observation.locations && observation.locations.length > 0) {
                // Use Set.has() for O(1) lookup instead of Array.includes() O(n)
                const hasMatchingLocation = observation.locations.some(location => {
                    if (!location.figuregroup_id) return false;
                    const locFiguregroupId = String(location.figuregroup_id);
                    const locLevelId = String(location.level_id);
                    return figuregroupIdsSet.has(locFiguregroupId) && locLevelId === levelIdStr;
                });
                if (hasMatchingLocation) {
                    observationIds.push(observationId);
                }
            }
        }
        
        return observationIds;
    }

    componentDidUpdate(prevProps) {
        mapManager(this.map, this.props, prevProps)
        if (prevProps.createBreakMode !== this.props.createBreakMode) {
            this.updateDrawControls(this.props.createBreakMode);
        }
    }

    handleWhatsAppLabelClick = () => {
        // Desactivar modo WhatsApp
        this.props.toggleSwitch(ToggleActions.WHATSAPP_OBSERVATIONS_MODE);
        // Limpiar selecciones y cerrar popup
        this.props.setSelectedWhatsAppObservations([]);
        this.props.setWhatsAppObservationsPopup({ 
            show: false, 
            close: false, 
            closeList: false, 
            expanded: false, 
            opacity: 1 
        });
    }

    render() {
        const showWhatsAppLabel = this.props.whatsappObservationsMode && 
                                   (!this.props.whatsappObservations?.popup?.show);
        
        return(
            <>
                <div className='main-background' ref={el => this.mapContainer = el} />
                {showWhatsAppLabel && (
                    <div 
                        className='whatsapp-mode-label'
                        onClick={this.handleWhatsAppLabelClick}
                        title="Click para desactivar"
                    >
                        <div className='whatsapp-mode-label-icon'>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                            </svg>
                        </div>
                        <span className='whatsapp-mode-label-text'>
                            Modo Observaciones de WhatsApp Activado
                        </span>
                        <div className='whatsapp-mode-label-pulse'></div>
                    </div>
                )}
            </>
        )
    } 
}

function mapStateToProps(state) {
    const mapStyle = process.env.REACT_APP_MAPBOX_STYLE;
    return {
        mapStyle       : mapStyle,
        projectData    : state.projectData,
        filters        : state.filters,
        whatsappFilters: state.whatsappObservations.filters,
        timelineSelect : state.filters.timelineSelect,
        day            : state.day,
        selectedItem   : state.selectedItem,
        loadedMap      : state.switches.loadedMap,
        projectUsers   : state.projectUsers,
        popUpMenu      : state.popUpMenu,
        paint          : state.paint,
        opacity        : state.opacity,
        fullBuilding   : state.switches.fullBuilding,
        timeline       : state.switches.timeline,
        plannedTimeline: state.switches.plannedTimeline,
        axles          : state.switches.axles,
        roadLabel      : state.switches.roadLabel,
        loadingLevelId : state.loadingLevelId,
        loadingLevel   : state.switches.loadingLevel,
        refreshSwitch  : state.switches.refresh,
        loadedLevels   : state.loadedLevels,
        featuresGroups : state.featuresGroups,
        cardsGroupsWithHistoricalObservations : state.cardsGroupsWithHistoricalObservations,
        createBreakMode  : state.switches.createBreakMode,
        whatsappObservationsMode : state.switches.whatsappObservationsMode,
        whatsappObservations : state.whatsappObservations,
    };
}

const actions = {
    toggleSwitch : toggleSwitch,
    selectedCardsGroupsChange : selectedCardsGroupsChange,
    popUpMenuChange : popUpMenuChange,
    visibleCardsGroupsChange : visibleCardsGroupsChange,
    visibleWhatsAppObservationsChange : visibleWhatsAppObservationsChange,
    setBreakCoordinatesModal : setBreakCoordinatesModal,
    deleteCards : deleteCards,
    popUpMessageChange : popUpMessageChange,
    editFigures : editFigures,
    editCards : editCards,
    editFeaturesGroup : editFeaturesGroup,
    setSelectedWhatsAppObservations : setSelectedWhatsAppObservations,
    setWhatsAppObservationsPopup : setWhatsAppObservationsPopup,
};

export default connect(mapStateToProps, actions)(Mapbox);